import {
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  BookingPermission,
  CouponPermission,
  CustomFieldPermission,
  InvoicePermission,
  RoomPermission,
  UserPermission,
  VendorPermission,
} from '../../core/models/entities/permission';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { ModalService } from '../../core/services/modal.service';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { map } from 'rxjs/operators';
import { LogoutBaseComponent } from '../../shared/components/logout-base/logout-base.component';
import { ChangelogService } from 'angular/app/changelog/services/changelog.service';
import { ChangelogModalComponent } from 'angular/app/shared/components/changelog-modal/changelog-modal.component';
import { APP_VERSION } from '../../core/constants';
import {
  selectAuthVendor,
  selectInvoicingOpenCount,
} from '../../core/store/reducers';
import { PaymentProvider } from '../../core/models/entities/vendor';
import { gsap } from 'gsap';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'app-sidebar-links',
  template: `
    <div class="flex min-h-full flex-col justify-between pt-4">
      <nav class="h-full overflow-y-auto">
        <div class="space-y-1 px-2">
          <!-- Sélecteur vendor mobile -->
          <div id="sidebar-users" class="pb-2 lg:hidden">
            @if (currentUser()?.vendors?.length > 1) {
              <app-vendor-selection></app-vendor-selection>
            }
            @if (currentUser()?.vendors?.length === 1) {
              <div class="py-4 text-sm uppercase">
                {{ currentUser()?.vendor?.name }}
              </div>
            }
          </div>

          <!-- Réservations -->
          <div id="sidebar-bookings">
            <ng-template [appHasPermission]="BookingPermission.Update">
              <app-sidebar-link
                label="Réservations"
                icon="calendar"
                routerLink="/reservations"
              >
                @if (pendingBookings$ | async) {
                  <app-badge
                    variant="primary"
                    appearance="solid"
                    [notification]="true"
                  >
                    {{
                      ((pendingBookingsCount$ | async) || 0) > 9
                        ? '9+'
                        : (pendingBookingsCount$ | async) || 0
                    }}
                  </app-badge>
                }
              </app-sidebar-link>
            </ng-template>
          </div>

          <!-- Salles -->
          <ng-template [appHasPermission]="RoomPermission.Store">
            <div id="sidebar-rooms">
              <app-sidebar-link
                label="Salles"
                icon="warehouse"
                routerLink="/salles"
              ></app-sidebar-link>
            </div>
          </ng-template>

          <!-- Codes promo -->
          <ng-template [appHasPermission]="CouponPermission.ViewAny">
            <div id="sidebar-coupons">
              <app-sidebar-link
                label="Codes promo"
                icon="percentage"
                routerLink="/codes-promo"
              ></app-sidebar-link>
            </div>
          </ng-template>

          <!-- Section Comptabilité -->
          <ng-template [appHasPermission]="InvoicePermission.ViewAny">
            <app-sidebar-section-title
              title="Comptabilité"
            ></app-sidebar-section-title>

            <ng-template [appHasPermission]="BookingPermission.Update">
              <div id="sidebar-invoicing">
                <app-sidebar-link
                  label="Facturation"
                  icon="calculator"
                  routerLink="/facturation"
                >
                  @if (openInvoicesCount()) {
                    <app-badge
                      variant="primary"
                      appearance="solid"
                      [notification]="true"
                    >
                      {{ openInvoicesCount() > 9 ? '9+' : openInvoicesCount() }}
                    </app-badge>
                  }
                </app-sidebar-link>
              </div>
            </ng-template>

            @if (vendor()?.accepts_online_payments) {
              <div id="sidebar-payment-provider">
                <app-sidebar-link
                  [label]="vendor().payment_provider | titleCase"
                  icon="arrow-up-right-from-square"
                  [href]="
                    vendor()?.payment_provider === PaymentProvider.Wallee
                      ? 'https://app-wallee.com/s/' +
                        vendor()?.wallee_space_id +
                        '/space'
                      : 'https://' +
                        (vendor().test_mode || vendor().is_ercos
                          ? 'test'
                          : vendor().payrexx_instance_name) +
                        '.pay.location-salles.ch/cadmin/index.php'
                  "
                  target="_blank"
                ></app-sidebar-link>
              </div>
            }
          </ng-template>

          <!-- Section Paramètres -->
          <ng-template [appHasPermission]="UserPermission.ViewAny">
            <app-sidebar-section-title
              title="Paramètres"
            ></app-sidebar-section-title>

            <!-- Utilisateurs -->
            <div id="sidebar-users">
              <app-sidebar-link
                label="Utilisateurs"
                icon="user-group"
                routerLink="/utilisateurs"
              ></app-sidebar-link>
            </div>

            <!-- Réglages (collapsible) -->
            <ng-template [appHasPermission]="VendorPermission.Update">
              <div id="sidebar-settings">
                <a
                  class="flex cursor-pointer items-center gap-x-3.5 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-800 no-underline hover:bg-gray-100"
                  (click)="toggleSettings()"
                >
                  <i class="fal fa-cog fa-fw text-base"></i>
                  <span class="translate-y-px">Réglages</span>
                  <i
                    class="fal fa-chevron-down size-4 transition-transform"
                    [ngClass]="{
                      'rotate-180': !settingsCollapsed(),
                    }"
                  ></i>
                </a>
              </div>

              <div class="h-0 overflow-hidden" #collapsibleContent>
                <div
                  class="my-1 ml-5 space-y-1 border-l-2 border-gray-200 pl-4"
                >
                  <app-sidebar-link
                    label="Groupes de tarifs"
                    routerLink="/reglages/groupes-de-tarifs"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    label="Conditions d'annulation"
                    routerLink="/reglages/conditions-annulation"
                    [indent]="true"
                  ></app-sidebar-link>

                  <ng-template
                    [appHasPermission]="CustomFieldPermission.ViewAny"
                  >
                    <ng-template appFeature="custom_fields" let-isBeta="isBeta">
                      <app-sidebar-link
                        label="Champs personnalisés"
                        routerLink="/reglages/champs-personnalises"
                        [indent]="true"
                      >
                        @if (isBeta) {
                          <span end class="ms-auto -translate-y-1 px-1">
                            <app-badge variant="pink">beta</app-badge>
                          </span>
                        }
                      </app-sidebar-link>
                    </ng-template>
                  </ng-template>

                  <app-sidebar-link
                    label="Ouverture calendrier"
                    routerLink="/reglages/ouverture-calendrier"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    label="Modèles communication"
                    routerLink="/reglages/modeles-communication"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    label="Modes de paiement"
                    routerLink="/reglages/modes-de-paiement"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    label="Sécurité"
                    routerLink="/reglages/securite"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    label="Mode test"
                    routerLink="/reglages/mode-test"
                    [indent]="true"
                  ></app-sidebar-link>
                </div>
              </div>

              <!-- Organisation -->
              <div id="sidebar-account">
                <app-sidebar-link
                  label="Organisation"
                  icon="building"
                  routerLink="/administration"
                ></app-sidebar-link>
              </div>
            </ng-template>
          </ng-template>

          <!-- Section Divers -->
          <app-sidebar-section-title title="Divers"></app-sidebar-section-title>

          <!-- Page publique -->
          @if (currentUser()) {
            <div id="sidebar-public-page">
              <app-sidebar-link
                label="Page publique"
                icon="arrow-up-right-from-square"
                [href]="'/louer-salle/' + currentUser().vendor.slug"
                target="_blank"
              ></app-sidebar-link>
            </div>
          }
        </div>
      </nav>

      <!-- Footer de la sidebar -->
      <div class="mt-4 border-t border-gray-200 p-4">
        @if (currentUser()) {
          <!-- User actions -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <button
                class="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                [routerLink]="['/compte']"
                routerLinkActive="bg-gray-100"
                data-cy="user-profile"
                title="Mon compte"
              >
                <i class="fal fa-user-pen"></i>
              </button>
              <button
                class="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                (click)="logout()"
                title="Déconnexion"
              >
                <i class="fal fa-power-off"></i>
              </button>
            </div>

            <button
              class="relative text-xs text-gray-500 hover:text-gray-700"
              (click)="openChangelogModal()"
            >
              <span>v{{ version }}</span>
              @if (changelogService.hasUnreadChanges) {
                <div
                  class="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-pink-500"
                ></div>
              }
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [],
  standalone: false,
})
export class SidebarLinksComponent extends LogoutBaseComponent {
  RoomPermission = RoomPermission;
  BookingPermission = BookingPermission;
  UserPermission = UserPermission;
  VendorPermission = VendorPermission;
  InvoicePermission = InvoicePermission;
  CouponPermission = CouponPermission;
  currentUser = this.store.selectSignal(fromStore.selectAuthUser);
  pendingBookings$ = this.store
    .select(fromStore.selectAllPendingBookingsCount)
    .pipe(map((pendingBookings) => pendingBookings > 0));
  pendingBookingsCount$ = this.store.select(
    fromStore.selectAllPendingBookingsCount,
  );
  openInvoicesCount = this.store.selectSignal(selectInvoicingOpenCount);
  vendor = this.store.selectSignal(selectAuthVendor);
  settingsCollapsed = signal(true);
  collapsibleContent = viewChild<ElementRef>('collapsibleContent');
  protected readonly PaymentProvider = PaymentProvider;
  version = APP_VERSION;
  router = inject(Router);
  route = inject(ActivatedRoute);

  constructor(
    private store: Store,
    public changelogService: ChangelogService,
    modalService: ModalService,
    authService: AuthService,
    toastService: ToastService,
  ) {
    super(modalService, authService, toastService);
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.toggleSettingsWhenNecessary();
      });
  }

  ngAfterViewInit() {
    this.toggleSettingsWhenNecessary();
  }

  openChangelogModal() {
    this.modalService.open(ChangelogModalComponent, {
      title: 'Notes de version',
      showActionButtons: false,
      size: 'xl',
    });
  }

  /**
   * Ferme le collapse des réglages si on quitte les réglages
   * Ouvre le collapse des réglages si on est arrivé sur la page par un autre lien (on n'a pas ouvert manuellement le menu)
   */
  toggleSettingsWhenNecessary() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const isChildRouteSettings =
      !!currentRoute.parent && this.router.url.startsWith('/reglages');

    if (isChildRouteSettings && this.settingsCollapsed()) {
      this.toggleSettings(); // on ouvre les réglages si ce n'est pas le cas
    } else if (!isChildRouteSettings && !this.settingsCollapsed()) {
      this.toggleSettings(); // on a quitté les réglages donc on ferme le collapse
    }
  }

  toggleSettings() {
    this.settingsCollapsed.set(!this.settingsCollapsed());
    const content = this.collapsibleContent().nativeElement;

    if (this.settingsCollapsed()) {
      gsap.to(content, { height: 0, duration: 0.3, ease: 'power2.inOut' });
    } else {
      gsap.to(content, { height: 'auto', duration: 0.3, ease: 'power2.inOut' });
    }
  }

  protected readonly CustomFieldPermission = CustomFieldPermission;
}
